import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { superAdminAPI } from '../../services/api';
import './LeadsList.scss';

const LeadsList = () => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({ status: 'all', search: '', page: 1 });
  const [viewingLead, setViewingLead] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['leads', filters],
    queryFn: () => superAdminAPI.getLeads(filters)
  });

  const leads = data?.data?.data?.leads || [];
  const pagination = data?.data?.data?.pagination || {};

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }) => superAdminAPI.updateLeadStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries(['leads']);
    }
  });

  const getStatusBadge = (status) => {
    const classes = {
      pending: 'badge-warning',
      booked: 'badge-info',
      completed: 'badge-success',
      cancelled: 'badge-danger'
    };
    return classes[status] || 'badge-secondary';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: '⏳',
      booked: '📅',
      completed: '✅',
      cancelled: '❌'
    };
    return icons[status] || '📋';
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleStatusUpdate = (leadId, newStatus) => {
    if (window.confirm(`Are you sure you want to mark this lead as ${newStatus}?`)) {
      updateStatusMutation.mutate({ id: leadId, status: newStatus });
    }
  };

  return (
    <div className="leads-page">
      <div className="page-header">
        <div className="header-left">
          <h1>Leads</h1>
          <p>Manage demo requests and call bookings</p>
        </div>
      </div>

      <div className="filters-bar">
        <input
          type="text"
          placeholder="Search by name, email, or company..."
          value={filters.search}
          onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }))}
          className="search-input"
        />
        <select
          value={filters.status}
          onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value, page: 1 }))}
          className="filter-select"
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="booked">Booked</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <div className="table-container">
        {isLoading ? (
          <div className="loading">Loading leads...</div>
        ) : error ? (
          <div className="error-state">
            <div className="error-icon">⚠️</div>
            <h3>Error loading leads</h3>
            <p>{error?.message || 'Failed to fetch leads. Please check if the backend is running.'}</p>
          </div>
        ) : leads.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <h3>No leads found</h3>
            <p>Leads from demo requests will appear here</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Company</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Team Size</th>
                  <th>Status</th>
                  <th>Submitted At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => (
                  <tr key={lead._id}>
                    <td>
                      <div className="name-cell">
                        <span className="name">{lead.fullName}</span>
                      </div>
                    </td>
                    <td>{lead.companyName}</td>
                    <td>
                      <a href={`mailto:${lead.email}`} className="email-link">
                        {lead.email}
                      </a>
                    </td>
                    <td>
                      <a href={`tel:${lead.phone}`} className="phone-link">
                        {lead.phone}
                      </a>
                    </td>
                    <td>
                      <span className="badge badge-info">{lead.teamSize}</span>
                    </td>
                    <td>
                      <span className={`badge ${getStatusBadge(lead.status)}`}>
                        {lead.status}
                      </span>
                    </td>
                    <td>
                      <div className="submission-info">
                        <span className="submission-date">
                          {new Date(lead.createdAt).toLocaleDateString()}
                        </span>
                        <span className="submission-time">
                          {formatTime(lead.createdAt)}
                        </span>
                        {lead.booking && (
                          <a
                            href="https://calendly.com/app/meetings"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="booking-link"
                            title="View booking in Calendly"
                          >
                            📅 Calendly
                          </a>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={() => setViewingLead(lead)}
                        >
                          View
                        </button>
                        {lead.status === 'booked' && (
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => handleStatusUpdate(lead._id, 'completed')}
                            disabled={updateStatusMutation.isPending}
                          >
                            Complete
                          </button>
                        )}
                        {(lead.status === 'pending' || lead.status === 'booked') && (
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleStatusUpdate(lead._id, 'cancelled')}
                            disabled={updateStatusMutation.isPending}
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {pagination.totalPages > 1 && (
        <div className="pagination">
          <button
            className="btn btn-outline"
            disabled={filters.page === 1}
            onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
          >
            Previous
          </button>
          <span className="page-info">
            Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
          </span>
          <button
            className="btn btn-outline"
            disabled={filters.page === pagination.totalPages}
            onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
          >
            Next
          </button>
        </div>
      )}

      {/* View Lead Modal - Improved UI */}
      {viewingLead && (
        <div className="lead-modal-overlay" onClick={() => setViewingLead(null)}>
          <div className="lead-modal" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="lead-modal-header">
              <div className="header-content">
                <div className="lead-avatar">
                  {viewingLead.fullName?.charAt(0)?.toUpperCase() || 'L'}
                </div>
                <div className="header-info">
                  <h2>{viewingLead.fullName}</h2>
                  <p className="company-name">{viewingLead.companyName}</p>
                </div>
              </div>
              <div className="header-status">
                <span className={`status-badge ${viewingLead.status}`}>
                  {getStatusIcon(viewingLead.status)} {viewingLead.status}
                </span>
              </div>
              <button className="modal-close-btn" onClick={() => setViewingLead(null)}>
                ×
              </button>
            </div>

            {/* Body */}
            <div className="lead-modal-body">
              {/* Quick Actions */}
              <div className="quick-actions">
                <a href={`mailto:${viewingLead.email}`} className="quick-action-btn email">
                  <span className="icon">📧</span>
                  <span>Send Email</span>
                </a>
                <a href={`tel:${viewingLead.phone}`} className="quick-action-btn phone">
                  <span className="icon">📞</span>
                  <span>Call</span>
                </a>
                <button
                  className="quick-action-btn copy"
                  onClick={() => {
                    navigator.clipboard.writeText(viewingLead.email);
                    alert('Email copied!');
                  }}
                >
                  <span className="icon">📋</span>
                  <span>Copy Email</span>
                </button>
              </div>

              {/* Contact Information */}
              <div className="info-section">
                <h3>
                  <span className="section-icon">👤</span>
                  Contact Information
                </h3>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="label">Full Name</span>
                    <span className="value">{viewingLead.fullName}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Email Address</span>
                    <a href={`mailto:${viewingLead.email}`} className="value link">
                      {viewingLead.email}
                    </a>
                  </div>
                  <div className="info-item">
                    <span className="label">Phone Number</span>
                    <a href={`tel:${viewingLead.phone}`} className="value link">
                      {viewingLead.phone}
                    </a>
                  </div>
                  <div className="info-item">
                    <span className="label">Company</span>
                    <span className="value">{viewingLead.companyName}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Team Size</span>
                    <span className="value team-size">{viewingLead.teamSize}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Submitted At</span>
                    <span className="value">{formatDateTime(viewingLead.createdAt)}</span>
                  </div>
                </div>
              </div>

              {/* Booking Status */}
              <div className="info-section">
                <h3>
                  <span className="section-icon">📅</span>
                  Booking Status
                </h3>
                {viewingLead.booking ? (
                  <div className="booking-card">
                    <div className="booking-status">
                      <div className="status-icon">✅</div>
                      <div className="status-info">
                        <h4>Booking Confirmed</h4>
                        <p className="status-text">Demo scheduled via Calendly</p>
                      </div>
                    </div>
                    <a
                      href="https://calendly.com/app/meetings"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary booking-link-btn"
                    >
                      📅 View in Calendly Dashboard
                    </a>
                    <p className="booking-hint">
                      Click to see date, time, meeting link and confirmation details
                    </p>
                  </div>
                ) : (
                  <div className="no-booking-card">
                    <div className="no-booking-icon">⏳</div>
                    <p>Waiting for booking</p>
                    <span>Lead has not selected a time slot yet</span>
                  </div>
                )}
              </div>

              {/* Timeline */}
              <div className="info-section">
                <h3>
                  <span className="section-icon">⏱️</span>
                  Timeline
                </h3>
                <div className="timeline">
                  <div className="timeline-item">
                    <div className="timeline-dot created"></div>
                    <div className="timeline-content">
                      <span className="timeline-label">Lead Created</span>
                      <span className="timeline-time">{formatDateTime(viewingLead.createdAt)}</span>
                    </div>
                  </div>
                  <div className="timeline-item">
                    <div className="timeline-dot updated"></div>
                    <div className="timeline-content">
                      <span className="timeline-label">Last Updated</span>
                      <span className="timeline-time">{formatDateTime(viewingLead.updatedAt)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="lead-modal-footer">
              <button className="btn btn-outline" onClick={() => setViewingLead(null)}>
                Close
              </button>
              {viewingLead.status === 'pending' && (
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    handleStatusUpdate(viewingLead._id, 'booked');
                    setViewingLead(null);
                  }}
                  disabled={updateStatusMutation.isPending}
                >
                  Mark as Booked
                </button>
              )}
              {viewingLead.status === 'booked' && (
                <button
                  className="btn btn-success"
                  onClick={() => {
                    handleStatusUpdate(viewingLead._id, 'completed');
                    setViewingLead(null);
                  }}
                  disabled={updateStatusMutation.isPending}
                >
                  ✓ Mark as Completed
                </button>
              )}
              {(viewingLead.status === 'pending' || viewingLead.status === 'booked') && (
                <button
                  className="btn btn-danger"
                  onClick={() => {
                    handleStatusUpdate(viewingLead._id, 'cancelled');
                    setViewingLead(null);
                  }}
                  disabled={updateStatusMutation.isPending}
                >
                  Cancel Lead
                </button>
              )}
              {viewingLead.status === 'cancelled' && (
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    handleStatusUpdate(viewingLead._id, 'pending');
                    setViewingLead(null);
                  }}
                  disabled={updateStatusMutation.isPending}
                >
                  Reactivate
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsList;